import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Star, Check, Tag, Loader2 } from 'lucide-react';

const PlanSelector = ({ courseId, courseName, coursePrice, onEnroll }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, [courseId]);

  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`/pricing-plans/${courseId}`);
      const activePlans = response.data.filter(p => p.is_active);
      setPlans(activePlans);
      
      // Auto-select first plan if available
      if (activePlans.length > 0 && !selectedPlan) {
        setSelectedPlan(activePlans[0]);
      }
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
      // Fallback to course price if no plans
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setValidatingCoupon(true);
    try {
      const amount = selectedPlan ? selectedPlan.price : coursePrice;
      const response = await axios.post('/validate-coupon', {
        code: couponCode.toUpperCase(),
        amount: amount
      });

      setCouponApplied(response.data);
      toast.success(`Coupon applied! You save ₹${response.data.discount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    toast.info('Coupon removed');
  };

  const calculatePerMonthCost = (price, days) => {
    const months = days / 30;
    return (price / months).toFixed(0);
  };

  const handleEnroll = () => {
    const finalAmount = couponApplied ? couponApplied.final_amount : (selectedPlan ? selectedPlan.price : coursePrice);
    
    onEnroll({
      pricing_plan_id: selectedPlan?.id,
      coupon_code: couponApplied ? couponCode.toUpperCase() : null,
      amount: finalAmount,
      original_amount: selectedPlan ? selectedPlan.price : coursePrice,
      plan_name: selectedPlan ? selectedPlan.plan_name : 'Standard Plan'
    });
  };

  const getDisplayPrice = () => {
    if (couponApplied) {
      return couponApplied.final_amount;
    }
    return selectedPlan ? selectedPlan.price : coursePrice;
  };

  const getOriginalPrice = () => {
    return selectedPlan ? selectedPlan.price : coursePrice;
  };

  return (
    <div className="space-y-4">
      {/* Loading State */}
      {loadingPlans ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading pricing plans...</span>
        </div>
      ) : (
        <>
          {/* Plan Selection */}
          {plans.length > 0 ? (
        <div>
          <h3 className="font-semibold mb-3">Select Subscription Duration</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative cursor-pointer transition-all ${
                  selectedPlan?.id === plan.id 
                    ? 'border-2 border-blue-500 shadow-lg' 
                    : 'border hover:border-blue-300'
                } ${plan.is_best_value ? 'border-blue-400' : ''}`}
                onClick={() => {
                  setSelectedPlan(plan);
                  setCouponApplied(null); // Reset coupon when changing plans
                }}
              >
                {plan.is_best_value && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Star className="w-3 h-3" /> Best Value
                    </span>
                  </div>
                )}
                
                <CardContent className="pt-6 pb-4">
                  <div className="text-center">
                    <h4 className="font-semibold text-lg mb-2">{plan.plan_name}</h4>
                    <div className="text-3xl font-bold text-blue-600 mb-1">
                      ₹{plan.price}
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      ₹{calculatePerMonthCost(plan.price, plan.duration_in_days)}/month
                    </p>
                    <p className="text-xs text-gray-600">
                      {plan.duration_in_days} days access
                    </p>
                    
                    {selectedPlan?.id === plan.id && (
                      <div className="mt-3 flex items-center justify-center text-blue-600 text-sm font-semibold">
                        <Check className="w-4 h-4 mr-1" /> Selected
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {coursePrice > 0 ? (
            <>
              <h3 className="font-semibold mb-2">Course Price</h3>
              <div className="text-3xl font-bold text-blue-600">₹{coursePrice}</div>
              <p className="text-sm text-gray-500">Standard plan</p>
            </>
          ) : (
            <>
              <h3 className="font-semibold mb-2">Pricing</h3>
              <p className="text-lg text-gray-600">Contact admin for pricing information</p>
            </>
          )}
        </div>
      )}

      {/* Coupon Code Section */}
      <div className="border-t pt-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Tag className="w-4 h-4" /> Have a Coupon Code?
        </h3>
        
        {!couponApplied ? (
          <div className="flex gap-2">
            <Input
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              disabled={validatingCoupon}
            />
            <Button 
              onClick={validateCoupon}
              disabled={validatingCoupon || !couponCode.trim()}
              variant="outline"
            >
              {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex justify-between items-center">
            <div>
              <p className="font-semibold text-green-800">{couponCode} Applied!</p>
              <p className="text-sm text-green-600">You save ₹{couponApplied.discount.toFixed(2)}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={removeCoupon}>
              Remove
            </Button>
          </div>
        )}
      </div>

      {/* Price Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span>Original Price:</span>
          <span>₹{getOriginalPrice()}</span>
        </div>
        
        {couponApplied && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount:</span>
            <span>- ₹{couponApplied.discount.toFixed(2)}</span>
          </div>
        )}
        
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Total:</span>
          <span className="text-blue-600">₹{getDisplayPrice()}</span>
        </div>
      </div>

      {/* Enroll Button */}
      <Button 
        onClick={handleEnroll}
        className="w-full"
        size="lg"
        disabled={loading}
      >
        {loading ? 'Processing...' : `Enroll Now - ₹${getDisplayPrice()}`}
      </Button>

      <p className="text-xs text-center text-gray-500">
        {selectedPlan ? `${selectedPlan.duration_in_days} days` : '150 days'} of full access to all course content
      </p>
      </>
      )}
    </div>
  );
};

export default PlanSelector;
